import type { AdminExhibitionPurpose } from '@repo/api-contracts';
import { GripVertical } from 'lucide-react';
import type { ReactElement } from 'react';
import { beginMoveDrag } from '@/shared/lib';
import { cn, DropdownMenu } from '@/shared/ui';

interface ExhibitionPurposeTabProps {
  readonly purpose: AdminExhibitionPurpose;
  readonly isSelected: boolean;
  readonly isDragging: boolean;
  readonly isBusy: boolean;
  readonly onSelect: () => void;
  readonly onDragStart: () => void;
  readonly onDrop: () => void;
  readonly onDragEnd: () => void;
  readonly onRename: () => void;
  readonly onDelete: () => void;
}

/** 목적 탭 하나 — 누르면 그 목적의 기획전 목록으로 바뀌고, 이름 변경·삭제는 미트볼 메뉴 안에 있다. */
export const ExhibitionPurposeTab = ({
  purpose,
  isSelected,
  isDragging,
  isBusy,
  onSelect,
  onDragStart,
  onDrop,
  onDragEnd,
  onRename,
  onDelete,
}: ExhibitionPurposeTabProps): ReactElement => (
  <li
    draggable={!isBusy}
    onDragStart={(event) => {
      if (beginMoveDrag(event, purpose.id)) {
        onDragStart();
      }
    }}
    onDragOver={(event) => event.preventDefault()}
    onDrop={onDrop}
    onDragEnd={onDragEnd}
    className={cn(
      'flex items-center gap-1 rounded-full border py-1 pl-2 pr-1',
      !isBusy && 'cursor-grab',
      isSelected && 'border-foreground bg-muted',
      isDragging && 'opacity-40',
    )}
  >
    <GripVertical className="size-3.5 text-muted-foreground" aria-hidden="true" />
    <button
      type="button"
      disabled={isBusy}
      onClick={onSelect}
      aria-current={isSelected ? 'true' : undefined}
      className="text-sm font-medium disabled:opacity-50"
    >
      {purpose.name}
    </button>
    {/* 요청 중에는 메뉴를 감춘다 — 잠긴 화면에서 이름 변경·삭제가 겹쳐 들어가지 않게. */}
    {isBusy ? null : (
      <DropdownMenu
        ariaLabel={`${purpose.name} 메뉴`}
        items={[
          { label: '이름 변경', onSelect: onRename },
          { label: '삭제', isDestructive: true, onSelect: onDelete },
        ]}
      />
    )}
  </li>
);
